import _pt from "prop-types";

/*
 * Copyright (C) 2024 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { useCallback, useState } from 'react';
import tinycolor from 'tinycolor2';
import formatMessage from '../../../../format-message';
import { Button } from '@instructure/ui-buttons';
import { ColorPreset, ColorMixer, ColorContrast } from '@instructure/ui-color-picker';
import { Flex } from '@instructure/ui-flex';
import { Pill } from '@instructure/ui-pill';
import { RadioInputGroup, RadioInput } from '@instructure/ui-radio-input';
import { Tabs } from '@instructure/ui-tabs';
import { Text } from '@instructure/ui-text';
import { ToggleDetails } from '@instructure/ui-toggle-details';
import { View } from '@instructure/ui-view';
import { isTransparent, getContrastStatus } from './colorUtils'; // this will hold colors the user picks during this session

const previouslyChosenColors = [];

const ColorPicker = _ref => {
  let {
    tabs,
    onCancel,
    onSave
  } = _ref;
  const [currFgColor, setCurrFgColor] = useState(tabs.foreground);
  const [currBgColor, setCurrBgColor] = useState(isTransparent(tabs.background) ? undefined : tabs.background);
  const [currBorderColor, setCurrBorderColor] = useState(tabs.border || '#00000000');
  const [activeTab, setActiveTab] = useState(tabs.foreground ? 'foreground' : 'background');
  const [customBackground, setCustomBackground] = useState(!isTransparent(tabs.background));
  const [customBorder, setCustomBorder] = useState(!isTransparent(tabs.border));
  const [defaultColors] = useState(() => {
    const fontcolor = window.getComputedStyle(document.documentElement).getPropertyValue('--ic-brand-font-color-dark') || '#000000';
    return [fontcolor, '#FFFFFF'];
  });
  const updatePreviousColors = useCallback(color => {
    if (previouslyChosenColors.includes(color)) return;
    if (defaultColors.includes(color)) return;
    if (color === `${defaultColors[0]}FF`) return;
    if (isTransparent(color)) return;
    previouslyChosenColors.unshift(color);
    if (previouslyChosenColors.length > 8) previouslyChosenColors.pop();
  }, [defaultColors]);
  const handleFgColorChange = useCallback(newColor => {
    setCurrFgColor(newColor);
  }, []);
  const handleBgColorChange = useCallback(newColor => {
    const c = tinycolor(newColor).toHexString();
    setCurrBgColor(c);
  }, []);
  const handleBorderColorChange = useCallback(newColor => {
    setCurrBorderColor(newColor);
  }, []);
  const handleTabChange = useCallback((_event, tabData) => {
    setActiveTab(tabData.id);
  }, []);
  const handleChangePickAColor = useCallback((_e, value) => {
    const isCustom = value === 'custom';

    if (activeTab === 'background') {
      setCustomBackground(isCustom);

      if (!isCustom) {
        setCurrBgColor(undefined);
      }
    } else if (activeTab === 'border') {
      setCustomBorder(isCustom);

      if (!isCustom) {
        setCurrBorderColor('#00000000');
      }
    }
  }, [activeTab]);
  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);
  const handleSubmit = useCallback(() => {
    setActiveTab(currFgColor ? 'foreground' : 'background');
    const newcolors = {};

    if (customBackground && currBgColor) {
      const c = tinycolor(currBgColor).toHexString();
      newcolors.bgcolor = c;
      updatePreviousColors(c);
    }

    if (currFgColor) {
      const c = tinycolor(currFgColor).toHexString();
      newcolors.fgcolor = c;
      updatePreviousColors(c);
    }

    if (currBorderColor) {
      newcolors.bordercolor = customBorder && !isTransparent(currBorderColor) ? currBorderColor : undefined;

      if (newcolors.bordercolor) {
        const c = tinycolor(newcolors.bordercolor).toHexString();
        newcolors.bordercolor = c;
        updatePreviousColors(c);
      }
    }

    onSave(newcolors);
  }, [currBgColor, currBorderColor, currFgColor, customBackground, customBorder, onSave, updatePreviousColors]);

  const renderColorMixer = (variant, enabled) => {
    let value = currBgColor;
    let onSelectColor = handleBgColorChange;

    if (variant === 'foreground') {
      value = currFgColor;
      onSelectColor = handleFgColorChange;
    }

    if (variant === 'border') {
      value = currBorderColor;
      onSelectColor = handleBorderColorChange;
    }

    if (isTransparent(value)) value = '#fff'; // or the ColorMixer will return a transparent color

    return /*#__PURE__*/React.createElement(ColorMixer, {
      "data-testid": "color-mixer",
      disabled: !enabled,
      value: value,
      withAlpha: false,
      onChange: onSelectColor,
      rgbRedInputScreenReaderLabel: formatMessage('Input field for red'),
      rgbGreenInputScreenReaderLabel: formatMessage('Input field for green'),
      rgbBlueInputScreenReaderLabel: formatMessage('Input field for blue'),
      rgbAlphaInputScreenReaderLabel: formatMessage('Input field for alpha'),
      colorSliderNavigationExplanationScreenReaderLabel: formatMessage("You are on a color slider. To navigate the slider left or right, use the 'A' and 'D' buttons respectively"),
      alphaSliderNavigationExplanationScreenReaderLabel: formatMessage("You are on an alpha slider. To navigate the slider left or right, use the 'A' and 'D' buttons respectively"),
      colorPaletteNavigationExplanationScreenReaderLabel: formatMessage("You are on a color palette. To navigate on the palette up, left, down or right, use the 'W', 'A', 'S' and 'D' buttons respectively")
    });
  };

  const renderColorPreset = (variant, enabled) => {
    let currColor = currBgColor;
    if (variant === 'foreground') currColor = currFgColor || defaultColors[1];
    if (variant === 'border') currColor = currBorderColor || '#00000000';
    let onSelectColor = handleBgColorChange;
    if (variant === 'foreground') onSelectColor = handleFgColorChange;
    if (variant === 'border') onSelectColor = handleBorderColorChange;
    return /*#__PURE__*/React.createElement(ColorPreset, {
      "data-testid": "color-preset",
      disabled: !enabled,
      label: formatMessage('Previously chosen colors'),
      colors: [...defaultColors, ...previouslyChosenColors],
      selected: currColor,
      onSelect: onSelectColor
    });
  };

  const getFirstColor = () => {
    let firstColor, firstColorLabel;

    if ('foreground' in tabs) {
      if (isTransparent(currFgColor)) firstColor = null;
      firstColor = currFgColor;
      firstColorLabel = formatMessage('Foreground');
    } else {
      if (!customBorder || isTransparent(currBorderColor)) firstColor = null;
      firstColor = currBorderColor;
      firstColorLabel = formatMessage('Border');
    }

    return {
      firstColor,
      firstColorLabel
    };
  };

  const renderColorContrastSummary = () => {
    const {
      firstColor
    } = getFirstColor();
    const ok = getContrastStatus(firstColor, currBgColor || '#fff');
    return /*#__PURE__*/React.createElement(Flex, {
      as: "div",
      gap: "x-large"
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "bold"
    }, formatMessage('Color Contrast')), /*#__PURE__*/React.createElement(Pill, {
      color: ok ? 'success' : 'danger'
    }, ok ? formatMessage('PASS') : formatMessage('FAIL')));
  };

  const renderColorContrast = () => {
    if (!('background' in tabs)) return null;
    if (!('foreground' in tabs || 'border' in tabs)) return null;
    if (!customBackground) return null;
    if ('border' in tabs && !customBorder) return null;
    const {
      firstColor,
      firstColorLabel
    } = getFirstColor();
    if (firstColor === null) return null;
    return /*#__PURE__*/React.createElement(ToggleDetails, {
      summary: renderColorContrastSummary(),
      "data-testid": "color-contrast-summary"
    }, /*#__PURE__*/React.createElement(View, {
      as: "div",
      margin: "small 0 0 0"
    }, /*#__PURE__*/React.createElement(ColorContrast, {
      "data-testid": "color-contrast",
      firstColor: firstColor,
      secondColor: currBgColor || '#fff',
      label: formatMessage('Color Contrast Ratio'),
      successLabel: formatMessage('PASS'),
      failureLabel: formatMessage('FAIL'),
      normalTextLabel: formatMessage('Normal text'),
      largeTextLabel: formatMessage('Large text'),
      graphicsTextLabel: formatMessage('Graphics text'),
      firstColorLabel: firstColorLabel,
      secondColorLabel: formatMessage('Background')
    })));
  };

  const renderTab = variant => {
    let choosersEnabled = true;

    if (variant === 'background') {
      choosersEnabled = customBackground;
    } else if (variant === 'border') {
      choosersEnabled = customBorder;
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, variant !== 'foreground' && /*#__PURE__*/React.createElement(View, {
      as: "div",
      margin: "0 0 small 0"
    }, /*#__PURE__*/React.createElement(RadioInputGroup, {
      layout: "columns",
      name: "pickcolor",
      description: formatMessage('Pick a color'),
      size: "small",
      value: choosersEnabled ? 'custom' : 'none',
      onChange: handleChangePickAColor
    }, /*#__PURE__*/React.createElement(RadioInput, {
      label: formatMessage('None'),
      value: "none"
    }), /*#__PURE__*/React.createElement(RadioInput, {
      label: formatMessage('Custom'),
      value: "custom"
    }))), renderColorMixer(variant, choosersEnabled), renderColorPreset(variant, choosersEnabled));
  };

  return /*#__PURE__*/React.createElement(View, {
    as: "div"
  }, /*#__PURE__*/React.createElement(View, {
    as: "div",
    padding: "small",
    "data-mce-component": true
  }, /*#__PURE__*/React.createElement(Tabs, {
    onRequestTabChange: handleTabChange
  }, 'foreground' in tabs && /*#__PURE__*/React.createElement(Tabs.Panel, {
    id: "foreground",
    renderTitle: formatMessage('Color'),
    isSelected: activeTab === 'foreground'
  }, renderTab('foreground')), 'background' in tabs && /*#__PURE__*/React.createElement(Tabs.Panel, {
    id: "background",
    renderTitle: formatMessage('Background'),
    isSelected: activeTab === 'background'
  }, renderTab('background')), 'border' in tabs && /*#__PURE__*/React.createElement(Tabs.Panel, {
    id: "border",
    renderTitle: formatMessage('Border'),
    isSelected: activeTab === 'border'
  }, renderTab('border'))), renderColorContrast()), /*#__PURE__*/React.createElement(View, {
    as: "div",
    background: "secondary",
    padding: "small",
    textAlign: "end"
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: handleCancel
  }, formatMessage('Cancel')), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSubmit,
    margin: "0 0 0 small",
    color: "primary"
  }, formatMessage('Apply'))));
};

ColorPicker.propTypes = {
  onCancel: _pt.func.isRequired,
  onSave: _pt.func.isRequired
};
export { ColorPicker };